import React, { useEffect, useState } from 'react';
import accessApi from '../services/accessApi';
import icpApi from '../services/icpApi';
import { formatDateTime, timeAgo } from '../utils/time';
import AccordionSection from './AccordionSection';
import Icon from './Icon';
import './IcpField.css';
import './RelationshipManagers.css';

const UNCLASSIFIED = 'unclassified';

// What the company's segment says, and where that came from
const describe = (icpName, icpSource) => {
  if (icpSource === null || icpSource === undefined) return 'Not yet checked';
  const label = icpName || 'Unclassified';
  if (icpSource === 'icp-import') return `${label} — from the ICP workbook`;
  if (icpSource === 'manual') return `${label} — set manually`;
  return `${label} — classified automatically`;
};

/**
 * The company's ICP segment, shown in the company panel and changed in place: one segment, or
 * Unclassified when none fits. Saving makes it a person's choice, which a later check won't change.
 */
const IcpField = ({ companyId, companyName, icpSegmentId, icpName, icpSource, onChange }) => {
  const [open, setOpen] = useState(true);
  const [editing, setEditing] = useState(false);
  // The segments to choose from, once they've loaded
  const [segments, setSegments] = useState(null);
  const [choice, setChoice] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  // The latest change to the company's ICP, from the audit log
  const [lastChange, setLastChange] = useState(null);
  const [savedCount, setSavedCount] = useState(0);

  useEffect(() => {
    setEditing(false);
    setError(null);
  }, [companyId]);

  useEffect(() => {
    setLastChange(null);
    if (!companyId) return undefined;
    let cancelled = false;
    accessApi.activity({ subject_type: 'company', subject_id: companyId, action: 'company.icp_', limit: 1 })
      .then(data => {
        if (!cancelled) setLastChange(data.events[0] || null);
      })
      // Only a hint: without it the field still says what the segment is
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [companyId, savedCount]);

  const startEditing = async () => {
    setOpen(true);
    setEditing(true);
    setError(null);
    setChoice(icpSegmentId ? String(icpSegmentId) : (icpSource ? UNCLASSIFIED : ''));
    setSegments(null);
    try {
      setSegments((await icpApi.listSegments()).segments);
    } catch (err) {
      setError(err.message);
      setSegments([]);
    }
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const saved = await icpApi.setCompanyIcp(companyId, choice === UNCLASSIFIED ? null : Number(choice));
      onChange(saved);
      setSavedCount(count => count + 1);
      setEditing(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <AccordionSection
      id="icp-field"
      className="rm-field"
      open={open}
      onToggle={() => setOpen(current => !current)}
      title="ICP segment"
      actions={!editing && (
        <button
          type="button"
          className="rm-icon-button"
          onClick={startEditing}
          aria-label={`Change the ICP segment of ${companyName || 'this company'}`}
          title="Change"
        >
          <Icon name="pencil" />
        </button>
      )}
    >
      {!editing && (
        <>
          <p className="icp-field__state">{describe(icpName, icpSource)}</p>
          {lastChange && (
            <p className="rm-field__note">
              Changed by {lastChange.actor.name}{' '}
              <span title={formatDateTime(lastChange.occurred_at)}>{timeAgo(lastChange.occurred_at)}</span>
            </p>
          )}
        </>
      )}

      {editing && (
        <div className="rm-picker">
          {segments === null ? (
            <p className="placeholder-text" role="status"><Icon name="loader" spin /> Loading...</p>
          ) : (
            <select
              className="rm-input"
              value={choice}
              onChange={(event) => setChoice(event.target.value)}
              disabled={saving}
              aria-label={`ICP segment of ${companyName || 'this company'}`}
            >
              {choice === '' && <option value="" disabled>Not yet checked</option>}
              {segments.map(segment => (
                <option key={segment.id} value={String(segment.id)}>{segment.name}</option>
              ))}
              <option value={UNCLASSIFIED}>Unclassified (no segment fits)</option>
            </select>
          )}

          {error && <p className="rm-error" role="alert"><Icon name="alert-triangle" /> {error}</p>}

          <div className="rm-buttons">
            <button
              type="button"
              className="rm-button rm-button--primary"
              onClick={save}
              disabled={saving || segments === null || choice === ''}
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button type="button" className="rm-button" onClick={() => setEditing(false)} disabled={saving}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </AccordionSection>
  );
};

export default IcpField;
